import { adminApi } from '@/features/admin';
import { profileApi } from '@/features/profile-management';
import { getStorageUrl } from '@/shared/lib';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Separator } from '@/shared/ui/separator';
import { Textarea } from '@/shared/ui/textarea';
import { ProfilePhoto } from '@/widgets/photo-lightbox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  MapPin,
  Save,
  Shield,
  Star,
  User,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const nodeStates = [
  { value: '1', label: 'Public' },
  { value: '2', label: 'Prive' },
  { value: '3', label: 'Supprime' },
];

const nodeTypes = [
  { value: '1', label: 'Utilisateur' },
  { value: '6', label: 'Moderateur' },
  { value: '8', label: 'Injoignable' },
  { value: '9', label: 'Premium' },
  { value: '10', label: 'VIP' },
  { value: '11', label: 'En attente contact' },
];

const noteOptions = [0, 1, 2, 3, 4, 5];

export default function AdminUserDetailPage() {
  const { nodeId = '' } = useParams<{ nodeId: string }>();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['admin-user', nodeId],
    queryFn: () => profileApi.getProfile(Number.parseInt(nodeId)),
  });

  const { data: adminUser } = useQuery({
    queryKey: ['admin-user-details', nodeId],
    queryFn: () => adminApi.getUser(Number.parseInt(nodeId)),
  });

  const verifyPhotoMutation = useMutation({
    mutationFn: (verified: boolean) => adminApi.verifyPhoto(Number.parseInt(nodeId), verified),
    onSuccess: () => {
      toast.success('Photo mise a jour');
      queryClient.invalidateQueries({ queryKey: ['admin-user', nodeId] });
    },
    onError: () => toast.error('Erreur lors de la mise a jour'),
  });

  const changeStatusMutation = useMutation({
    mutationFn: (statusId: number) => adminApi.changeStatus(Number.parseInt(nodeId), statusId),
    onSuccess: () => {
      toast.success('Statut mis a jour');
      queryClient.invalidateQueries({ queryKey: ['admin-user', nodeId] });
    },
    onError: () => toast.error('Erreur lors de la mise a jour'),
  });

  const changeRoleMutation = useMutation({
    mutationFn: (roleId: number) => adminApi.changeRole(Number.parseInt(nodeId), roleId),
    onSuccess: () => {
      toast.success('Role mis a jour');
      queryClient.invalidateQueries({ queryKey: ['admin-user', nodeId] });
    },
    onError: () => toast.error('Erreur lors de la mise a jour'),
  });

  const addNoteMutation = useMutation({
    mutationFn: (note: number) => adminApi.addNote(Number.parseInt(nodeId), note),
    onSuccess: () => {
      toast.success('Note mise a jour');
      queryClient.invalidateQueries({ queryKey: ['admin-user', nodeId] });
    },
    onError: () => toast.error('Erreur lors de la mise a jour'),
  });

  const addCommentMutation = useMutation({
    mutationFn: (comment: string) => adminApi.addComment(Number.parseInt(nodeId), comment),
    onSuccess: () => {
      toast.success('Commentaire enregistre');
      setComment('');
    },
    onError: () => toast.error("Erreur lors de l'enregistrement"),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild>
          <Link to="/admin/users">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Link>
        </Button>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild>
          <Link to="/admin/users">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Link>
        </Button>
        <Card>
          <CardContent className="p-8 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold">Utilisateur non trouve</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" asChild>
        <Link to="/admin/users">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour a la liste
        </Link>
      </Button>

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">
          {profile.username}
          <span className="text-muted-foreground text-lg ml-2">#{nodeId}</span>
        </h1>
        <div className="flex gap-2">
          <Badge variant={profile.isPhotoVerified ? 'default' : 'destructive'}>
            {profile.isPhotoVerified ? 'Photo validee' : 'Photo non validee'}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Info */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Profil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Full cropped photo (3:4), click = full screen: moderators must see it clearly */}
            <ProfilePhoto
              src={getStorageUrl(profile.avatarPath)}
              alt={profile.username}
              fallback={profile.username?.charAt(0).toUpperCase() || 'U'}
              className="mx-auto w-full max-w-xs rounded-lg"
            />

            <div className="text-center">
              <h2 className="text-xl font-bold">{profile.username}</h2>
              <p className="text-muted-foreground">
                {profile.age} ans
                {adminUser?.gender ? ` - ${adminUser.gender === 1 ? 'Homme' : 'Femme'}` : ''}
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              {profile.city && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {profile.city}, {profile.region}
                </div>
              )}
              {profile.profession && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {profile.profession}
                </div>
              )}
              {Number(profile.nationality) > 0 && (
                <div className="flex items-center gap-2 text-sm" data-testid="admin-nationality">
                  <img
                    src={`/flags/${profile.nationality}.png`}
                    alt=""
                    width={18}
                    height={12}
                    className="rounded-sm"
                  />
                  Nationalité : {profile.nationalityLabel}
                </div>
              )}
              {Number(profile.origin) > 0 && (
                <div className="flex items-center gap-2 text-sm" data-testid="admin-origin">
                  <img
                    src={`/flags/${profile.origin}.png`}
                    alt=""
                    width={18}
                    height={12}
                    className="rounded-sm"
                  />
                  Origine : {profile.originLabel}
                </div>
              )}
              {profile.lastActivity && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  Actif{' '}
                  {formatDistanceToNow(new Date(profile.lastActivity), {
                    addSuffix: true,
                    locale: fr,
                  })}
                </div>
              )}
            </div>

            <Separator />
            <div data-testid="admin-description">
              <p className="mb-1 text-sm font-medium">Ce que je recherche</p>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {profile.description || 'Non renseigné'}
              </p>
            </div>

            {/* Star Rating */}
            {Number(profile.note) > 0 && (
              <div className="flex items-center justify-center gap-1">
                {Array.from({ length: Number(profile.note) }).map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Actions */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Actions d'administration
            </CardTitle>
            <CardDescription>Modifier les parametres de l'utilisateur</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Photo Verification */}
            <div className="space-y-2">
              <Label>Validation de la photo</Label>
              <div className="flex gap-2">
                <Button
                  variant={profile.isPhotoVerified ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => verifyPhotoMutation.mutate(true)}
                  disabled={verifyPhotoMutation.isPending}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Valider
                </Button>
                <Button
                  variant={!profile.isPhotoVerified ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={() => verifyPhotoMutation.mutate(false)}
                  disabled={verifyPhotoMutation.isPending}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Rejeter
                </Button>
              </div>
            </div>

            <Separator />

            {/* Status */}
            <div className="space-y-2">
              <Label>Statut du compte</Label>
              <Select
                defaultValue={String(adminUser?.nodeState || 1)}
                onValueChange={(value) => changeStatusMutation.mutate(Number.parseInt(value))}
              >
                <SelectTrigger className="w-full md:w-64">
                  <SelectValue placeholder="Changer le statut" />
                </SelectTrigger>
                <SelectContent>
                  {nodeStates.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label>Type de compte</Label>
              <Select
                defaultValue={String(adminUser?.nodeType || 1)}
                onValueChange={(value) => changeRoleMutation.mutate(Number.parseInt(value))}
              >
                <SelectTrigger className="w-full md:w-64">
                  <SelectValue placeholder="Changer le role" />
                </SelectTrigger>
                <SelectContent>
                  {nodeTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Note */}
            <div className="space-y-2">
              <Label>Note (etoiles)</Label>
              <div className="flex gap-1">
                {noteOptions.map((note) => (
                  <Button
                    key={note}
                    variant="ghost"
                    size="sm"
                    onClick={() => addNoteMutation.mutate(note)}
                    disabled={addNoteMutation.isPending}
                  >
                    {note === 0 ? (
                      <XCircle className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <div className="flex">
                        {Array.from({ length: note }).map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              Number(profile.note) >= note
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-muted-foreground'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Admin Comment */}
            <div className="space-y-2">
              <Label>Commentaire administratif</Label>
              <Textarea
                placeholder="Ajouter un commentaire interne..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <Button
                onClick={() => addCommentMutation.mutate(comment)}
                disabled={!comment || addCommentMutation.isPending}
              >
                <Save className="h-4 w-4 mr-1" />
                Enregistrer le commentaire
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle>Liens rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to={`/profile/${nodeId}`} target="_blank">
                Voir le profil public
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
